import React, { type FC } from 'react'
import { t } from 'i18next'
import { isNil, last, round } from 'lodash'
import { environment } from '@awell/libs-web/environment'

import type { Activity } from '../../../../hooks/useCalculationActivity'
import { ActivityAction, CalculationResult } from '../../types'

interface ResultsTabProps {
  activity: Activity
  results: CalculationResult[]
}

export const ResultsTab: FC<ResultsTabProps> = ({ activity, results }) => {
  const resultItems = results.map(({ subresult_id, value, unit, status }) => {
    const displayValue = Number.isNaN(Number(value)) ? value : round(Number(value), 2)

    return (
      <div key={subresult_id} className="flex flex-col gap-1">
        <div className="text-sm text-slate-500">{subresult_id}</div>
        {status === 'CALCULATED' ? (
          <div key={subresult_id}>
            <div className="text-base font-medium">
              {`${displayValue}`} {unit}
            </div>
          </div>
        ) : (
          <div key={subresult_id}>
            <div className="text-sm text-slate-500">{status}</div>
          </div>
        )}
      </div>
    )
  })

  const subActivity = last(activity?.sub_activities)

  const isJsonParsable = (string: string) => {
    if (isNil(string)) {
      return false
    }
    try {
      JSON.parse(string)
    } catch (e) {
      return false
    }
    return true
  }

  return (
    <>
      <div>
        {subActivity?.action === ActivityAction.Computed && (
          <div className="pb-2">
            <div className="flex flex-col gap-2">
              <div className="text-base">{t('calculation_disclaimer')}</div>
              <div className="flex flex-col gap-1">
                <div className="text-sm font-medium text-slate-600">{t('calculation_documentation_label')}</div>
                <a
                  href={`${environment.urls.calculations_explorer}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-500 hover:underline"
                >
                  {t('view_calculation_documentation')}
                </a>
              </div>
            </div>
          </div>
        )}
        {subActivity?.action === ActivityAction.Failed && (
          <div className="pb-2">
            <div className="text-base text-red-500">{t('calculation_results_error')}</div>
            {t('error_message')}
            {': '}
            <div className="whitespace-pre-wrap text-sm font-medium">
              {isJsonParsable(subActivity?.error ?? '')
                ? JSON.parse(subActivity?.error ?? '')?.error?.message
                : subActivity?.error}
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-slate-200 pt-2 flex flex-col gap-2">{resultItems}</div>
    </>
  )
}
